import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { PublishedVersion } from 'icreate/common/models/versions-history';
import { VersionNameInfoDialogComponent } from '../version-name-info-dialog/version-name-info-dialog.component';
import { versionDialogValidator } from '@models/models-validators/version-dialog-validator';
import { getValidationErrors, mapErrors, ValidationErrorMap } from 'icreate/common/validators';

@Component({
    selector: 'app-version-name-page',
    templateUrl: './version-name-page.component.html',
    styleUrls: ['./version-name-page.component.css'],
    standalone: false,
})
export class VersionNamePageComponent implements OnInit {
    @Input() isMajorUpdate: boolean;
    @Input() isTrialRun: boolean;
    @Input() referenceVersion: string;
    @Input() versions: PublishedVersion[];
    @Input() inApprovalPage: boolean = false;
    @Output() onVersionNameChange = new EventEmitter<string>();

    recommendedVersion: string;
    customizeVersion: boolean = false;
    versionName: string = '';
    validationErrors: ValidationErrorMap = {};
    hasError: boolean;

    constructor(private dialog: MatDialog) {}

    ngOnInit(): void {
        if (!this.referenceVersion) {
            this.referenceVersion = '0.0';
        }
        const version = this.referenceVersion.split('.');

        if (this.inApprovalPage) {
            this.recommendedVersion = version.join('.');
            return;
        }

        if (this.isMajorUpdate) {
            version[0] = (Number(version[0]) + 1).toString();
        } else {
            version[1] = (Number(version[1].replace(/\D/g, '')) + 1).toString();
        }

        if (isNaN(Number(version[1]))) {
            version[1] = version[1].slice(0, -1);
        }
        if (this.isTrialRun) {
            version[1] = version[1] + 'a';
        }

        this.recommendedVersion = version.join('.');
        if (!this.versionIsRepeated(this.recommendedVersion)) {
            this.onVersionNameChange.emit(this.recommendedVersion);
        }

        this.validateVersion(this.versionName);
    }

    openInfo() {
        this.dialog.open(VersionNameInfoDialogComponent, {
            width: '474px',
            data: { versionName: this.recommendedVersion },
        });
    }

    toggleClick(event: InputEvent) {
        this.customizeVersion = (event.currentTarget as HTMLInputElement).checked;
        let versionToCheck = this.recommendedVersion;
        if (this.customizeVersion) {
            versionToCheck = this.versionName;
        }
        if (!this.versionIsRepeated(versionToCheck)) {
            this.onVersionNameChange.emit(versionToCheck);
        }
    }

    versionIsRepeated(versionName) {
        if (this.versions && this.versions.length > 0)
            return this.versions.filter(version => versionName === version.name).length > 0;
        return false;
    }

    updateVersionName(version: string) {
        this.versionName = version;

        this.validateVersion(this.versionName);

        let versionToCheck = this.customizeVersion ? this.versionName : this.recommendedVersion;

        if (!this.hasError) {
            this.onVersionNameChange.emit(versionToCheck);
        } else {
            this.onVersionNameChange.emit(null);
        }
    }

    validateVersion(versionName: string) {
        const object = { versionName: versionName };
        const errors = getValidationErrors(versionDialogValidator(), object);
        const mappedErrors: { [key: string]: { message: string } } = mapErrors(errors) || {};

        const isRepeated = this.versionIsRepeated(versionName);

        if (isRepeated) {
            mappedErrors['versionName'] = {
                message: 'version.create.dialog.number.input.repeated',
            };
        }

        this.validationErrors = Object.keys(mappedErrors).length > 0 ? mappedErrors : null;

        this.hasError = !!this.validationErrors?.versionName;
    }

    public get validatorFunction() {
        return versionDialogValidator;
    }
}
